import React, { ComponentProps } from 'react'
import Box from '@mui/material/Box'
import { ValueNarrativeCategoryTabContent } from './ValueNarrativeCategoryTabContent'
import { ValueNarrativesAllCategoriesTabContent } from './ValueNarrativesAllCategoriesTabContent'
import { ValueNarrative, ValueNarrativeCategory } from '../../queries/useGetValueNarrativesQuery'
import { EmptyPlaceholder } from '../../../../shared/components/EmptyPlaceholder'
import Tabs from '../../../../shared/components/Tabs/Tabs'
import useValueNarrativeDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useValueNarrativeDisplayName'

type Props = {
    isAIEnabled: boolean
    dataWithPseudoCategories: ValueNarrativeCategory[]
    dataWithoutPseudoCategories: ValueNarrativeCategory[]
    selectedCategory: ValueNarrativeCategory
    selectedNarrative: ValueNarrative | null
    handleSelectCategory: (categoryId: string) => void
    handleSelectNarrative: (categoryId: string, narrativeId: string) => void
    handleSelectMessage: (categoryId: string, narrativeId: string, messageId: string) => void
}

export const ValueNarrativeCategoryTabsWrapper = ({
    isAIEnabled,
    selectedCategory,
    selectedNarrative,
    handleSelectCategory,
    handleSelectNarrative,
    handleSelectMessage,
    dataWithPseudoCategories,
    dataWithoutPseudoCategories,
}: Props) => {
    const valueNarrativeDisplayName = useValueNarrativeDisplayName()

    const getTabView = (category: ValueNarrativeCategory) => {
        const isEmpty = !category.narratives.length

        if (isEmpty) {
            return (
                <Box pt={4}>
                    <EmptyPlaceholder
                        title={`No ${valueNarrativeDisplayName.plural} to display`}
                        subtitle={
                            category._id === 'archived'
                                ? `You have no archived ${valueNarrativeDisplayName.plural}`
                                : `Click 'Add New' on the left panel to create a new ${valueNarrativeDisplayName.singular}`
                        }
                    />
                </Box>
            )
        }

        if (category._id === 'all') {
            return (
                <ValueNarrativesAllCategoriesTabContent
                    categories={dataWithoutPseudoCategories.map((cat) => ({
                        ...cat,
                        narratives: cat.narratives.filter((narrative) => !narrative.archived),
                    }))}
                    handleSelectNarrative={handleSelectNarrative}
                />
            )
        }

        if (category._id === 'archived') {
            return (
                <ValueNarrativesAllCategoriesTabContent
                    categories={dataWithoutPseudoCategories.map((cat) => ({
                        ...cat,
                        narratives: cat.narratives.filter((narrative) => !!narrative.archived),
                    }))}
                    handleSelectNarrative={handleSelectNarrative}
                />
            )
        }

        if (selectedNarrative) {
            return (
                <ValueNarrativeCategoryTabContent
                    isAIEnabled={isAIEnabled}
                    selectedCategory={{
                        ...selectedCategory,
                        narratives: selectedCategory.narratives.filter((narrative) => !narrative.archived),
                    }}
                    selectedNarrative={selectedNarrative}
                    handleSelectNarrative={handleSelectNarrative}
                    handleSelectMessage={handleSelectMessage}
                />
            )
        }

        return (
            <ValueNarrativesAllCategoriesTabContent
                categories={[
                    {
                        ...selectedCategory,
                        narratives: selectedCategory.narratives.filter((narrative) => !narrative.archived),
                    },
                    {
                        ...dataWithPseudoCategories.find((cat) => cat._id === 'archived'),
                        narratives: selectedCategory.narratives.filter((narrative) => !!narrative.archived),
                    },
                ]}
                handleSelectNarrative={handleSelectNarrative}
            />
        )
    }

    const getTabLabel = (category: ValueNarrativeCategory) => {
        const numberOfValueNarratives =
            (category._id === 'archived'
                ? category.narratives.length
                : category.narratives.filter((narrative) => !narrative.archived).length) || 0

        return `${category.name} ${numberOfValueNarratives ? `(${numberOfValueNarratives})` : ''}`
    }

    const tabs: ComponentProps<typeof Tabs>['tabs'] = [
        ...dataWithPseudoCategories.map((category) => ({
            id: category._id,
            label: getTabLabel(category),
            view: getTabView(category),
        })),
    ]

    return (
        <Box display="flex" flex={1} ml={-2}>
            <Tabs
                tabs={tabs}
                selectedTabId={selectedCategory._id}
                allowClickOnSelectedTab
                onTabChange={(tabId) => {
                    handleSelectCategory(tabId)
                }}
            />
        </Box>
    )
}
